/** Marks a custom horizontal scrollbar thumb (e.g. OverlayThumbX) so dragging it counts. */
export const SCROLL_X_THUMB_ATTR = 'data-scroll-x-thumb'

const HORIZONTAL_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End'])

/**
 * Calls `onIntent` once, the first time the user scrolls `scroller` sideways themselves: a
 * horizontal wheel/trackpad gesture (or Shift+wheel), a Left/Right/Home/End key, a press on
 * the native horizontal scrollbar or a `[data-scroll-x-thumb]` inside `root`, or a mostly
 * horizontal touch pan. Vertical scrolling doesn't count. Returns a cleanup.
 */
export function watchHorizontalScrollIntent(
  root: HTMLElement,
  scroller: HTMLElement,
  onIntent: () => void
): () => void {
  let touchStart: { x: number; y: number } | null = null
  let done = false
  const fire = () => {
    if (done) return
    done = true
    cleanup()
    onIntent()
  }

  const onWheel = (e: WheelEvent) => {
    if (e.deltaX !== 0 || e.shiftKey) fire()
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (HORIZONTAL_KEYS.has(e.key)) fire()
  }
  const onPointerDown = (e: PointerEvent) => {
    const target = e.target as Element | null
    if (target?.closest(`[${SCROLL_X_THUMB_ATTR}]`)) return fire()
    // A press on the scroller itself below its content box is its horizontal scrollbar.
    if (target === scroller && e.offsetY > scroller.clientHeight) fire()
  }
  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0]
    touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null
  }
  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0]
    if (!touchStart || !touch) return
    const dx = Math.abs(touch.clientX - touchStart.x)
    const dy = Math.abs(touch.clientY - touchStart.y)
    if (dx > 4 && dx > dy) fire()
  }

  root.addEventListener('wheel', onWheel, { passive: true })
  root.addEventListener('keydown', onKeyDown)
  root.addEventListener('pointerdown', onPointerDown)
  root.addEventListener('touchstart', onTouchStart, { passive: true })
  root.addEventListener('touchmove', onTouchMove, { passive: true })

  function cleanup() {
    root.removeEventListener('wheel', onWheel)
    root.removeEventListener('keydown', onKeyDown)
    root.removeEventListener('pointerdown', onPointerDown)
    root.removeEventListener('touchstart', onTouchStart)
    root.removeEventListener('touchmove', onTouchMove)
  }
  return cleanup
}
